import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ArrowRight, Sparkles, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const YucaipaCA = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Mobile Car Detailing in Yucaipa, CA | Precision Mobile Detailing';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile car detailing in Yucaipa, CA. Serving Chapman Heights, Wildwood Canyon, and all Yucaipa neighborhoods. Premium auto detailing at your home or business.');
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Precision Mobile Detailing - Yucaipa",
      "image": "https://customer-assets.emergentagent.com/job_shine-mobile-14/artifacts/48ukm1vz_image.png",
      "@id": "",
      "url": window.location.href,
      "telephone": businessInfo.phone,
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Yucaipa",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "areaServed": {
        "@type": "City",
        "name": "Yucaipa",
        "addressRegion": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 34.0336,
        "longitude": -117.0431
      },
      "description": "Professional mobile car detailing service in Yucaipa, CA",
      "serviceType": ["Auto Detailing", "Car Detailing", "Mobile Detailing"]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { name: 'Full Detail Package', link: '/services' },
    { name: 'Mini Detail', link: '/services' },
    { name: 'Paint Enhancement', link: '/services' },
    { name: 'Clay and Seal', link: '/services' }
  ];

  const faqs = [
    {
      question: 'Do you provide mobile detailing in Chapman Heights and the Yucaipa foothills?',
      answer: 'Yes! We serve all of Yucaipa including Chapman Heights, the foothills communities, Wildwood Canyon, and everywhere in between. We bring full mobile detailing to your driveway no matter where you are in Yucaipa.'
    },
    {
      question: 'How far in advance should I book car detailing in Yucaipa?',
      answer: 'We recommend booking at least a few days in advance, especially during weekends. However, we often have same-week availability. Call or book online to check our schedule.'
    },
    {
      question: 'What do you need from me for mobile detailing?',
      answer: 'Just a safe place for us to park near your vehicle. We bring everything else—water, equipment, professional products, and expertise.'
    },
    {
      question: 'Can you handle trucks and larger vehicles in Yucaipa?',
      answer: 'Absolutely! We detail sedans, SUVs, trucks, and larger vehicles. Our pricing adjusts based on vehicle size, and we have the equipment to handle any vehicle.'
    },
    {
      question: 'Is mobile car detailing in Yucaipa worth it?',
      answer: 'Definitely. You get professional-quality results without the time and hassle of driving to a shop. We come to you, work while you relax or run errands, and leave your car looking incredible.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1649042250037-122e56bc16b1?w=1920&q=80')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">Yucaipa, California</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Mobile Car Detailing in Yucaipa, CA
            </h1>
            
            <p className="text-xl mb-8 text-gray-300 leading-relaxed">
              Premium mobile auto detailing delivered to your home or business in Yucaipa. 
              From Chapman Heights to Wildwood Canyon and beyond, we bring professional car care to your driveway.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
                <Link to="/booking">
                  Book Yucaipa Detailing <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Rating • 30 Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{businessInfo.daysOpen} • {businessInfo.hours}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Mobile Detailing in Yucaipa */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Expert Auto Detailing for Yucaipa Vehicles</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Yucaipa drivers know the value of a well-maintained vehicle—especially with the dust and dirt that comes 
                from living near the foothills and canyons. Precision Mobile Detailing brings thorough, professional car 
                care directly to Yucaipa homes and businesses, saving you time while delivering exceptional results.
              </p>
              <p>
                Whether you live in Chapman Heights, near Wildwood Canyon, or anywhere in Yucaipa, we make detailing 
                effortless. No need to schedule your day around a trip to the car wash—we handle everything onsite 
                with professional equipment, premium products, and years of experience.
              </p>
              <p>
                Our mobile detailing service is perfect for busy families, working professionals, and anyone who values 
                their time. Schedule your appointment, and we'll show up ready to transform your vehicle while you stay 
                comfortable at home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Yucaipa Loves Mobile Detailing */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Why Yucaipa Residents Choose Mobile Detailing</h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              Mobile detailing is the smarter way to keep your vehicle looking great—especially in Yucaipa's scenic but dusty environment.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Saves You Time</h3>
                <p className="text-gray-600">Skip the drive to town. We come to your driveway, so you can focus on family, work, or just relaxing at home.</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Deep Clean Results</h3>
                <p className="text-gray-600">Living near the hills means dust and dirt. Our thorough detailing removes grime from every surface, inside and out.</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Protects Your Investment</h3>
                <p className="text-gray-600">Regular detailing protects your paint, interior, and resale value. We use premium products designed to preserve your vehicle.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in Yucaipa */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Car Detailing Services Available in Yucaipa</h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              From quick refreshes to complete transformations, we have detailing packages for every vehicle and budget.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {services.map((service, idx) => (
                <Card key={idx} className="border hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                        <Link to={service.link} className="text-black font-semibold hover:underline">
                          See Details →
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
                <Link to="/services">
                  Explore All Services & Pricing
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">What Makes Precision Mobile Detailing Different</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Attention to Detail</h3>
                  <p className="text-gray-600">We don't cut corners. Every inch of your vehicle gets the care and attention it deserves—from wheels to windshield.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Trusted by Yucaipa</h3>
                  <p className="text-gray-600">Locally trusted with a 5.0-star reputation. Reliable, professional, and committed to exceeding expectations every time.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">We Come to You</h3>
                  <p className="text-gray-600">Mobile service means total convenience. Home, work, or anywhere in Yucaipa—we bring the detailing shop to your location.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area within Yucaipa */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Yucaipa Neighborhoods We Serve</h2>
            <p className="text-center text-gray-600 mb-8 text-lg">
              Professional mobile car detailing throughout Yucaipa and the surrounding foothills area.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {['Chapman Heights', 'Wildwood Canyon', 'Oak Glen Area', 'Calimesa Border', 'County Line Road', 'Yucaipa Valley'].map((area, idx) => (
                <span key={idx} className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700">
                  {area}
                </span>
              ))}
            </div>

            <p className="text-center text-gray-600 mt-8">
              We also provide mobile detailing in nearby <Link to="/service-areas/redlands-ca" className="text-black font-semibold hover:underline">Redlands</Link> and <Link to="/service-areas/loma-linda-ca" className="text-black font-semibold hover:underline">Loma Linda</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Yucaipa Mobile Detailing FAQs</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Book Mobile Detailing in Yucaipa Now</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Professional car detailing at your doorstep. Schedule your appointment and experience the Precision difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Book Now <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/service-areas">
                View All Service Areas
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YucaipaCA;
